// ===========================================
// The children editor's rows (enquiries plan §7, Phase 2)
// ===========================================
// What `ChildrenEditor` edits and how it goes to the API. A row keeps its server `id`, so the
// PATCH updates that child instead of replacing it (the server keeps a child's link to a student).

import type { EnquiryChildInput } from '../../../api/enquiry.api';

/** The server's limit (`ENQUIRY_CHILDREN_MAX`). */
export const CHILDREN_MAX = 10;

export interface ChildRow {
  /** Local key for React; never sent. */
  key: string;
  /** The server's id for an existing child. */
  id?: string;
  firstName: string;
  lastName: string;
  /** As typed: '' = not given. */
  age: string;
  interest: string;
  /** Enrolled as a student: cannot be removed. */
  locked?: boolean;
}

let nextKey = 0;
export const newChildRow = (): ChildRow => ({ key: `new-${nextKey++}`, firstName: '', lastName: '', age: '', interest: '' });

/** An existing child as an editable row. */
export function childRowFrom(child: {
  id: string;
  firstName: string | null;
  lastName: string | null;
  age: number | null;
  interest: string | null;
  studentId?: string | null;
}): ChildRow {
  return {
    key: child.id,
    id: child.id,
    firstName: child.firstName ?? '',
    lastName: child.lastName ?? '',
    age: child.age == null ? '' : String(child.age),
    interest: child.interest ?? '',
    locked: !!child.studentId,
  };
}

const isBlank = (r: ChildRow) => !r.firstName.trim() && !r.lastName.trim() && !r.age.trim() && !r.interest.trim();

/** An age the server accepts: a whole number 0 to 120, or blank. */
export const ageError = (age: string): string | null =>
  age.trim() === '' || (/^\d{1,3}$/.test(age.trim()) && Number(age) <= 120) ? null : 'Use a whole number';

/**
 * The rows as the API takes them. A blank NEW row is dropped; an existing row is always sent
 * (blanking a child's fields keeps the child: Remove is the way to drop one).
 */
export function childrenPayload(rows: ChildRow[]): EnquiryChildInput[] {
  return rows
    .filter((r) => r.id || !isBlank(r))
    .map((r) => ({
      ...(r.id ? { id: r.id } : {}),
      firstName: r.firstName.trim() || null,
      lastName: r.lastName.trim() || null,
      age: r.age.trim() === '' ? null : Number(r.age),
      interest: r.interest.trim() || null,
    }));
}
